import React, { createContext, useState, useEffect, useContext } from 'react';
import themeConfig from './themeConfig'; // Import the static configuration

// Create a context for theme management
export const ThemeContext = createContext({
  currentTheme: themeConfig.theme,
  currentLayout: themeConfig.layout,
  setTheme: () => {},
  setLayout: () => {}
});

// Custom hook to use the theme context
export function useTheme() {
  return useContext(ThemeContext);
}

// Expose theme functions globally for admin use outside React
if (typeof window !== 'undefined') {
  window.techtokTheme = {
    setTheme: function(theme) {
      try {
        // Only allow runtime changes if enabled in config
        if (!themeConfig.allowRuntimeChanges) {
          console.log("Runtime theme changes are disabled. Edit themeConfig.js instead.");
          return false;
        }
        
        // Apply theme to document body
        document.body.classList.remove('default', 'corporate-blue', 'moba');
        document.body.classList.add(theme);
        
        // Dispatch an event for React components
        window.dispatchEvent(new CustomEvent('theme-changed', { detail: { theme, type: 'theme' } }));
        
        return true;
      } catch (error) {
        console.error('Error setting theme:', error);
        return false;
      }
    },
    setLayout: function(layout) {
      try {
        // Only allow runtime changes if enabled in config
        if (!themeConfig.allowRuntimeChanges) {
          console.log("Runtime layout changes are disabled. Edit themeConfig.js instead.");
          return false;
        }
        
        // Apply layout to document body
        document.body.classList.remove('centered-layout', 'left-aligned-layout');
        document.body.classList.add(`${layout}-layout`);
        
        // Dispatch an event for React components
        window.dispatchEvent(new CustomEvent('theme-changed', { detail: { layout, type: 'layout' } }));
        
        return true;
      } catch (error) {
        console.error('Error setting layout:', error);
        return false;
      }
    },
    getTheme: function() {
      return themeConfig.theme;
    },
    getLayout: function() {
      return themeConfig.layout;
    }
  };
}

// Theme provider component
export function ThemeProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState(themeConfig.theme);
  const [currentLayout, setCurrentLayout] = useState(themeConfig.layout);
  
  // Only add event listeners if runtime changes are allowed
  useEffect(() => {
    if (themeConfig.allowRuntimeChanges) {
      const handleExternalThemeChange = (event) => {
        if (event.detail.type === 'theme') {
          setCurrentTheme(event.detail.theme);
        } else if (event.detail.type === 'layout') {
          setCurrentLayout(event.detail.layout);
        }
      };
      
      window.addEventListener('theme-changed', handleExternalThemeChange);
      
      return () => {
        window.removeEventListener('theme-changed', handleExternalThemeChange);
      };
    }
  }, []);
  
  // Apply theme and layout classes on component mount
  useEffect(() => {
    console.log("Applying theme/layout from static config:", currentTheme, currentLayout);
    
    // Apply theme
    document.body.classList.remove('default', 'corporate-blue', 'moba');
    document.body.classList.add(currentTheme);
    
    // Apply layout
    document.body.classList.remove('centered-layout', 'left-aligned-layout');
    document.body.classList.add(`${currentLayout}-layout`);
    
  }, [currentTheme, currentLayout]);
  
  // Theme context value
  const contextValue = {
    currentTheme,
    currentLayout,
    setTheme: (theme) => {
      if (!themeConfig.allowRuntimeChanges) {
        console.log("Runtime theme changes are disabled. Edit themeConfig.js instead.");
        return;
      }
      
      setCurrentTheme(theme);
      if (window.techtokTheme) {
        window.techtokTheme.setTheme(theme);
      }
    },
    setLayout: (layout) => {
      if (!themeConfig.allowRuntimeChanges) {
        console.log("Runtime layout changes are disabled. Edit themeConfig.js instead.");
        return;
      }
      
      setCurrentLayout(layout);
      if (window.techtokTheme) {
        window.techtokTheme.setLayout(layout);
      }
    }
  };
  
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeProvider;